import type { PermissionName } from '@/api/types';
import type { ShellLink, ShellNavGroup, ShellNavItem } from '@/components/mative-shell/types';

/**
 * Le sezioni del portale e il menu che le apre.
 *
 * ── Gli indirizzi, senza una libreria di routing ────────────────────────────
 * Il guscio ragiona per indirizzi (`href`, `pathname`): qui l'indirizzo e' il
 * nome della sezione, `/plan`, e il collegamento cambia sezione
 * (`layout/SectionShell.tsx`, D26). Dal 29/10/2026 (D69) e' anche l'indirizzo
 * nella barra del browser: digitato, ricaricato o aperto in una scheda nuova
 * porta alla sua sezione, anche passando dal login, che ricorda dove si andava.
 * Le regole in `lib/indirizzi.ts`; una sezione ha un indirizzo solo, senza
 * sottopagine, e una libreria di routing per questo non serve.
 *
 * ── Come si aggiunge una sezione ────────────────────────────────────────────
 * L'id in `SECTIONS`, la voce in un gruppo di `GROUPS` con il `permission` che
 * serve per usarla, la schermata in `SCHERMATE` di `App.tsx`. L'ordine dei
 * gruppi e delle voci e' l'ordine del menu.
 *
 * ── Il permesso qui non e' una misura di sicurezza ──────────────────────────
 * Decide cosa **disegnare**. Chi chiamasse l'API a mano prenderebbe 403
 * comunque, perche' a farlo valere e' la dipendenza sulla rotta. Serve a non
 * mostrare voci che portano a un errore. Un elenco solo filtrato per permesso,
 * e non un elenco per ruolo come in PayHub: in MME i ruoli sono cumulativi
 * (`mme-admin` li contiene tutti) e il backend ragiona per permessi
 * (`/api/me/permissions`).
 */

export const SECTIONS = [
  'dashboard',
  'pubblico',
  'hub',
  'cards',
  'articoli',
  'temi',
  'plan',
  'published',
  'report',
  'notifications',
  'audit',
  'settings',
  'session',
] as const;

export type SectionId = (typeof SECTIONS)[number];

/** La prima voce e' la schermata d'apertura (D10, spec §21-22). */
export const HOME_SECTION: SectionId = 'dashboard';

export function hrefOf(section: SectionId): string {
  return `/${section}`;
}

/** La sezione di un indirizzo del guscio; `undefined` se non ne e' una. */
export function sectionOf(href: string): SectionId | undefined {
  const id = href.replace(/^\/+/, '').split(/[/?#]/)[0];
  return SECTIONS.find((section) => section === id);
}

type NavEntry = ShellNavItem & { permission?: PermissionName };

const GROUPS: { id: string; label: string; items: NavEntry[] }[] = [
  {
    id: 'contenuti',
    label: 'Contenuti',
    items: [
      {
        href: hrefOf('dashboard'),
        label: 'Dashboard',
        icon: 'layout-dashboard',
        description: "Cosa chiede attenzione e com'è andata la settimana",
      },
      {
        // D49: a chi parlano le campagne, prima di scriverle.
        href: hrefOf('pubblico'),
        label: 'Pubblico',
        icon: 'users',
        description: 'A chi parlano le campagne: aziende, persone che decidono, offerta e mix',
      },
      {
        href: hrefOf('hub'),
        label: 'Content Hub',
        icon: 'pen',
        description: 'Dove i contenuti nascono, si rivedono e si preparano',
      },
      {
        href: hrefOf('plan'),
        label: 'Piano editoriale',
        icon: 'calendar',
        // D42 (19/10/2026): la voce Da pubblicare e' finita qui dentro, nella scheda dell'uscita.
        description: 'Il calendario delle uscite: si pianifica, si approva e si pubblica',
      },
      {
        href: hrefOf('published'),
        label: 'Pubblicato',
        icon: 'check-circle',
        description: 'Cosa è uscito, dove, quando e con quale esito',
      },
      {
        href: hrefOf('report'),
        // «Analytics» dal 19/10/2026; la sezione resta `report`, come la rotta.
        label: 'Analytics',
        icon: 'chart',
        description: 'Da dove arrivano visite e richieste di demo',
      },
    ],
  },
  {
    // D29: le grafiche fatte nel portale, una voce per tipo.
    id: 'grafica',
    label: 'Grafica',
    items: [
      {
        href: hrefOf('cards'),
        label: 'Scheda prodotto',
        icon: 'monitor',
        description: 'Le immagini dei prodotti: schermate, richiami e testi per i post',
      },
      {
        href: hrefOf('articoli'),
        label: 'Articolo',
        icon: 'file-text',
        description: 'Le immagini degli articoli: uno sfondo e un titolo',
      },
      {
        // D47: i temi delle schede e degli articoli, da cambiare o da creare.
        href: hrefOf('temi'),
        label: 'Temi',
        icon: 'palette',
        description: 'I fondi, i colori e i caratteri di schede e articoli',
      },
    ],
  },
  {
    id: 'amministrazione',
    label: 'Amministrazione',
    items: [
      {
        href: hrefOf('notifications'),
        label: 'Notifiche',
        icon: 'bell',
        description: 'Errori dei flussi di n8n e di Synapsis ML',
      },
      {
        href: hrefOf('audit'),
        label: 'Audit Log',
        icon: 'scroll',
        description: 'Chi ha fatto cosa, quando, e con quale esito',
      },
      {
        href: hrefOf('settings'),
        label: 'Impostazioni',
        icon: 'settings',
        // Per tutti dalla 0009: la linguetta Server MCP (i token personali) e' di ognuno. Collegamenti e
        // Automazioni restano `settings.read`, e la pagina le mostra solo a chi ce l'ha.
        description: 'Canali collegati, automazioni e server MCP',
      },
    ],
  },
];

/**
 * Il menu per questi permessi. Un gruppo rimasto senza voci sparisce: un titolo
 * sopra il niente sembra un difetto della barra.
 */
export function navigationFor(
  permissions: Partial<Record<PermissionName, boolean>> | undefined,
): ShellNavGroup[] {
  return GROUPS.map((group) => ({
    id: group.id,
    label: group.label,
    items: group.items
      .filter((item) => item.permission === undefined || permissions?.[item.permission] === true)
      .map(({ permission: _permission, ...item }) => item),
  })).filter((group) => group.items.length > 0);
}

/**
 * Nel menu dell'account. Password, nome e verifica in due passaggi vivono su
 * Keycloak e non si duplicano qui (ADR 0002): il primo collegamento porta alla
 * console del realm. `consoleUrl` arriva da chi monta il guscio, perche' dipende
 * dalla configurazione a runtime.
 */
export function accountLinks(consoleUrl: string): ShellLink[] {
  return [
    {
      label: 'Il tuo account',
      href: consoleUrl,
      icon: 'user-cog',
      description: 'Nome, password e verifica in due passaggi, su Keycloak',
      external: true,
    },
    {
      label: 'La tua sessione',
      href: hrefOf('session'),
      icon: 'shield',
      description: 'I ruoli e i permessi con cui stai lavorando',
    },
  ];
}
